/**
 * Response Formatter Utility
 * 
 * Cleans and formats chatbot responses to be more user-friendly
 * - Removes markdown formatting
 * - Cleans up excessive formatting
 * - Makes responses more conversational
 */

/**
 * Clean and format chatbot responses for better UX
 * Keep existing structure but make formatting cleaner
 * 
 * @param content - Raw chatbot response content
 * @returns Cleaned and formatted response with proper HTML
 */
export const cleanChatbotResponse = (content: string): string => {
  if (!content) return content

  let cleaned = content

  // Remove markdown headers (## How to solve this:, ### Step 1, etc.)
  cleaned = cleaned.replace(/^#{1,6}\s+(.+)$/gm, '$1')
  
  // Keep the structure but clean up formatting
  // Convert **text** to <strong>text</strong> for proper bold formatting
  cleaned = cleaned.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  
  // Clean up numbered lists - keep them but make cleaner
  cleaned = cleaned.replace(/^\d+\.\s+/gm, '• ')
  
  // Clean up sub-bullet points (indented bullets)
  cleaned = cleaned.replace(/^\s+[-*]\s+/gm, '  • ')
  
  // Standardize main bullet points
  cleaned = cleaned.replace(/^[\s]*[-*+]\s+/gm, '• ')
  
  // Clean up link formatting - keep links but remove markdown
  cleaned = cleaned.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>')
  
  // Remove excessive line breaks (more than 2 consecutive)
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n')
  
  // Remove common formal ending phrases
  cleaned = cleaned.replace(/For more detailed guidance,?\s*/gi, '')
  cleaned = cleaned.replace(/You can refer to the full article.*$/gim, '')
  cleaned = cleaned.replace(/If you need further assistance.*$/gim, '')
  cleaned = cleaned.replace(/feel free to reach out!?\s*$/gim, '')
  
  // Clean up whitespace
  cleaned = cleaned.replace(/^\s*\n+/, '').replace(/\n+\s*$/, '')
  cleaned = cleaned.replace(/^\s*$/gm, '')
  cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n')
  
  return cleaned.trim()
}

/**
 * Example transformation:
 * 
 * INPUT:
 * ## How to solve this:
 * 
 * To integrate EasySell with PageFly, you can follow these steps:
 * 
 * 1. **Install PageFly:** First, ensure that you have the PageFly Landing Page Builder installed in your store.
 * 2. **Access EasySell COD Form & Upsells Element:**
 *    - Open your PageFly Editor.
 *    - Go to the left toolbar and select "Add third party elements."
 *    - Click on the "Edit your 3rd party elements" button.
 * 
 * For more detailed guidance, you can refer to the full article [here](https://help.tyslo.com/).
 * 
 * If you need further assistance, feel free to reach out!
 * 
 * OUTPUT:
 * How to solve this:
 * 
 * To integrate EasySell with PageFly, you can follow these steps:
 * 
 * • <strong>Install PageFly:</strong> First, ensure that you have the PageFly Landing Page Builder installed in your store.
 * • <strong>Access EasySell COD Form & Upsells Element:</strong>
 *   • Open your PageFly Editor.
 *   • Go to the left toolbar and select "Add third party elements."
 *   • Click on the "Edit your 3rd party elements" button.
 */